import { EnvelopeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { RowMenu } from '../../billing/RowMenu';
import type { UserListItem, UsersSortBy } from '../../../services/users';
import { LastActivity } from './LastActivity';
import { PracticesCell } from './PracticesCell';
import { RoleBadge } from './RoleBadge';
import { UserCell } from './UserCell';

export const USERS_TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
export const USERS_TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

export interface BuildUserColumnsOptions {
    onEditUser: (user: UserListItem) => void;
    onSendPasswordReset: (user: UserListItem) => void;
    onDeleteUser: (user: UserListItem) => void;
}

function menuItemsFor(
    user: UserListItem,
    { onEditUser, onSendPasswordReset, onDeleteUser }: BuildUserColumnsOptions
) {
    return [
        {
            label: 'Edit user',
            icon: <PencilIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onEditUser(user)
        },
        {
            label: 'Send password reset',
            icon: <EnvelopeIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onSendPasswordReset(user)
        },
        {
            label: 'Delete user',
            icon: <TrashIcon className="h-4 w-4" />,
            tone: 'danger' as const,
            onClick: () => onDeleteUser(user)
        }
    ];
}

// Only "Last activity" has a server-side sort equivalent this round - the
// other headers stay static labels, same as before the migration.
export function buildUserColumns(
    options: BuildUserColumnsOptions
): DataTableColumn<UserListItem, UsersSortBy>[] {
    return [
        {
            key: 'user',
            label: 'User',
            width: '32%',
            cellClassName: USERS_TD_PADDING,
            headerClassName: USERS_TH_PADDING,
            render: (user) => <UserCell name={user.fullname} email={user.email} />
        },
        {
            key: 'role',
            label: 'Role',
            width: '10%',
            cellClassName: USERS_TD_PADDING,
            headerClassName: USERS_TH_PADDING,
            render: (user) => <RoleBadge isAdmin={user.isAdmin} />
        },
        {
            key: 'practices',
            label: 'Practices',
            width: '34%',
            cellClassName: USERS_TD_PADDING,
            headerClassName: USERS_TH_PADDING,
            render: (user) => <PracticesCell email={user.email} practices={user.practices} />
        },
        {
            key: 'lastActivity',
            label: 'Last activity',
            width: '16%',
            sortable: true,
            sortKey: 'activity',
            cellClassName: USERS_TD_PADDING,
            headerClassName: `${USERS_TH_PADDING} whitespace-nowrap`,
            render: (user) => <LastActivity lastActivityAt={user.lastActivityAt} />
        },
        {
            key: 'menu',
            label: '',
            width: '8%',
            // No overflow-hidden on the menu cell (unlike USERS_TD_PADDING):
            // it would clip the RowMenu dropdown, which is absolutely
            // positioned inside this cell, entirely out of view.
            cellClassName: 'px-3 last:pr-4',
            headerClassName: USERS_TH_PADDING,
            stopClickPropagation: true,
            render: (user) => (
                <div className="flex justify-end">
                    <RowMenu items={menuItemsFor(user, options)} />
                </div>
            )
        }
    ];
}
